import React, { useState } from "react"
import { track } from "../../analytics/mixpanel"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const inputClass =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-600"
const primaryBtn =
  "inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const secondaryBtn =
  "inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"

// Shared Shopify Partner API credential form. Used by both the onboarding
// wizard and the settings connection panel — POSTs org id + access token, the
// server probes synchronously and returns the final status. The token field is
// write-only: never populated from the server, blanked on success.
export function ShopifyConnectForm({
  connectPath,
  copy,
  partnerId = null,
  initialOrgId = "",
  onSuccess,
  onCancel = null,
  cancelLabel,
}) {
  const [orgId, setOrgId] = useState(initialOrgId)
  const [accessToken, setAccessToken] = useState("")
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!orgId.trim() || !accessToken.trim() || loading) return
    setLoading(true)
    setErrors([])
    track("Shopify Connect Submitted", { partner_id: partnerId })
    try {
      const res = await fetch(connectPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          shopify_connection: { organization_id: orgId.trim(), access_token: accessToken.trim() },
        }),
      })
      const data = await res.json()
      if (res.ok) {
        setAccessToken("")
        onSuccess(data)
      } else {
        setErrors(data.errors || [copy.errorGeneric])
      }
    } catch {
      setErrors([copy.errorGeneric])
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div>
        <label htmlFor="shopify-org-id" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {copy.orgLabel}
        </label>
        <input
          id="shopify-org-id"
          type="text"
          inputMode="numeric"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          placeholder={copy.orgPlaceholder}
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor="shopify-access-token" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {copy.tokenLabel}
        </label>
        <input
          id="shopify-access-token"
          type="password"
          autoComplete="off"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder={copy.tokenPlaceholder}
          className={inputClass}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{copy.tokenHint}</p>
      </div>
      {errors.length > 0 && (
        <ul className="list-inside list-disc text-xs text-red-500 dark:text-red-400">
          {errors.map((err, i) => <li key={i}>{err}</li>)}
        </ul>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={loading} className={secondaryBtn}>
            {cancelLabel}
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !orgId.trim() || !accessToken.trim()}
          className={primaryBtn}
        >
          {loading ? copy.connecting : copy.submit}
        </button>
      </div>
    </form>
  )
}
